import React, { PropsWithChildren } from 'react'
import { ArrowDropDown, ArrowRight, ExpandLess, ExpandMore, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { alpha, createTheme, ThemeOptions, ThemeProvider } from '@mui/material'
import { StyledEngineProvider } from '@mui/material/styles'

import { ShadingValue } from '../types/material-ui'

const generatePalette = () => {
  const centralPalette = {
    type: 'light',
    primary: {
      main: '#1976D2', // rgb(25, 118, 210)
      dark: '#1565C0', // rgb(21, 101, 192)
      shades: {} as Record<ShadingValue, string>,
    },
    secondary: {
      main: 'rgba(0, 0, 0, 0.6)',
      shades: {} as Record<ShadingValue, string>,
    },
    success: {
      main: '#2E7D32', // rgb(46, 125, 50)
      light: '#4CAF50', // rgb(76, 175, 80)
      shades: {} as Record<ShadingValue, string>,
    },
    info: {
      main: '#0288D1', // rgb(2, 136, 209)
      light: '#03A9F4', // rgb(3, 169, 244)
      dark: '#01579B', // rgb(1, 87, 155)
      shades: {} as Record<ShadingValue, string>,
    },
    warning: {
      main: '#ED6C02', // rgb(237, 108, 2)
      light: '#FF9800', // rgb(255, 152, 0)
      shades: {} as Record<ShadingValue, string>,
      lightShades: {} as Record<ShadingValue, string>,
    },
    error: {
      main: '#D32F2F', // rgb(211, 47, 47)
      shades: {} as Record<ShadingValue, string>,
    },
    blue: {
      main: '#264A60',
      variant1: '#264A60',
      variant2: '#6F99A5',
      variant3: '#1F447E',
      variant4: '#3387B2',
      variant5: '#1F2756',
      shades: {} as Record<ShadingValue, string>,
    },
    action: {
      hover: 'rgba(0, 0, 0, 0.04)',
    },
    surface: {
      main: '#F7FBFF', // Surface
      variant1: '#1B1D20', // On Surface
      variant2: '#FBFDFF', // Surface Variant
      variant3: '#45474F', // On Surface Variant
      shades: {} as Record<ShadingValue, string>,
    },
  }

  const shades = [4, 8, 12, 16, 30]
  shades.forEach((percentage: number) => {
    centralPalette.primary.shades[`${percentage}p`] = alpha(centralPalette.primary.main, percentage / 100)
    centralPalette.secondary.shades[`${percentage}p`] = alpha(centralPalette.secondary.main, percentage / 100)
    centralPalette.success.shades[`${percentage}p`] = alpha(centralPalette.success.main, percentage / 100)
    centralPalette.error.shades[`${percentage}p`] = alpha(centralPalette.error.main, percentage / 100)
    centralPalette.info.shades[`${percentage}p`] = alpha(centralPalette.info.main, percentage / 100)
    centralPalette.warning.shades[`${percentage}p`] = alpha(centralPalette.warning.main, percentage / 100)
    centralPalette.warning.lightShades[`${percentage}p`] = alpha(centralPalette.warning.light, percentage / 100)
  })

  return centralPalette
}

const centralPalette = generatePalette()

// global overrides
const components = {
  MuiCssBaseline: {
    styleOverrides: {
      a: {
        color: centralPalette.primary.main,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '50px',
      },
    },
  },
  MuiButtonGroup: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        boxShadow: 'none',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '40px',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '8px',
      },
    },
  },
  MuiChip: {
    variants: [
      {
        props: { variant: 'semiround' as const },
        style: {
          borderRadius: '8px',
        },
      },
    ],
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        border: 0,
      },
      detailPanelToggleCell: {
        '&.Mui-disabled': {
          display: 'none',
        },
      },
      columnSeparator: {
        '&.MuiDataGrid-columnSeparator--sideRight': {
          display: 'none',
        },
        '&.MuiDataGrid-columnSeparator--resizable': {
          display: 'flex',
        },
      },
      cell: {
        display: 'flex',
        alignItems: 'center',
        '&:focus': {
          outline: 'none',
        },
      },
      columnHeader: {
        '&:focus': {
          outline: 'none',
        },
      },
    },
    defaultProps: {
      slots: {
        columnSortedAscendingIcon: () => <KeyboardArrowDown fontSize="small" />,
        columnSortedDescendingIcon: () => <KeyboardArrowUp fontSize="small" />,
        treeDataCollapseIcon: () => <ArrowDropDown />,
        treeDataExpandIcon: () => <ArrowRight />,
        detailPanelExpandIcon: () => <ExpandMore fontSize="small" />,
        detailPanelCollapseIcon: () => <ExpandLess fontSize="small" />,
      },
      disableColumnMenu: true,
      disableColumnFilter: true,
      disableVirtualization: globalThis.isJest, // Disable virtualization when running tests
    },
  },
} satisfies ThemeOptions['components']

export const centralTheme = createTheme({
  mixins: {
    toolbar: {
      minHeight: '80px',
    },
  },
  typography: {
    fontFamily: 'Work Sans',
    h6: {
      fontWeight: 500,
      fontSize: '20px',
      lineHeight: '160%',
    },
    caption: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '166%',
      letterSpacing: '0.4px',
    },
    buttonSmall: {
      textTransform: 'uppercase',
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '22px',
      letterSpacing: '0.46px',
    },
    subtitle1: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '175%',
      letterSpacing: '0.15px',
    },
    subtitle2: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '157%',
      letterSpacing: '0.1px',
    },
    body1: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
    },
    body2: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },
    overline: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '266%',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
  },
  palette: {
    ...centralPalette,
  },
  shape: {
    extraLargeBorderRadius: 32,
    largeBorderRadius: 16,
    mediumBorderRadius: 8,
    borderRadius: 4,
  },
  components,
})

const MuiThemeProvider: React.FC<PropsWithChildren> = ({ children }) => (
  <StyledEngineProvider injectFirst={true}>
    <ThemeProvider theme={centralTheme}>{children}</ThemeProvider>
  </StyledEngineProvider>
)

export default MuiThemeProvider
